import { useEffect, useState } from 'react';
import { Delete as DeleteIcon, Plus, X } from 'lucide-react';
import {
  appendFreePriceKey,
  defaultFreePriceDesignation,
  freePriceAmount,
} from './freePriceSale';

type Props = {
  categoryName: string;
  onAdd: (designation: string, priceTtc: number) => void;
  onClose: () => void;
};

function formatTnd(value: number): string {
  return `${value.toLocaleString('fr-FR', {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  })} TND`;
}

export function FreePriceProductModal({
  categoryName,
  onAdd,
  onClose,
}: Props) {
  const [designation, setDesignation] = useState(() =>
    defaultFreePriceDesignation(categoryName),
  );
  const [rawPrice, setRawPrice] = useState('');
  const priceTtc = freePriceAmount(rawPrice);
  const canAdd = designation.trim().length > 0 && priceTtc > 0;

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-3"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="free-price-title"
        className="w-full max-w-xs overflow-hidden rounded-2xl border border-gray-700 bg-gray-900 shadow-2xl"
      >
        <div className="flex items-center justify-between border-b border-gray-800 px-5 py-4">
          <div>
            <h2 id="free-price-title" className="text-sm font-bold text-white">
              {defaultFreePriceDesignation(categoryName)}
            </h2>
            <p className="text-[10px] text-gray-500">
              Saisir un nom et un prix libre
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-800 text-gray-400 transition-colors hover:bg-gray-700 hover:text-white"
          >
            <X size={13} />
          </button>
        </div>

        <div className="px-5 pb-2 pt-4">
          <label
            htmlFor="free-price-designation"
            className="text-[10px] font-bold uppercase tracking-wide text-gray-500"
          >
            Désignation
          </label>
          <input
            id="free-price-designation"
            autoFocus
            value={designation}
            onChange={(event) => setDesignation(event.target.value)}
            maxLength={40}
            className="mt-1 w-full rounded-xl border border-gray-700 bg-gray-800 px-3 py-2.5 text-sm text-white outline-none focus:border-[#0D7377] focus:ring-2 focus:ring-[#0D7377]/40"
          />
        </div>

        <div className="px-5 py-3 text-center">
          <div className="min-h-12 text-4xl font-black tracking-tight tabular-nums">
            {rawPrice ? (
              <span className="text-white">{formatTnd(priceTtc)}</span>
            ) : (
              <span className="text-gray-700">0,000 TND</span>
            )}
          </div>
        </div>

        <div className="px-5 pb-2">
          <div className="grid grid-cols-3 gap-1.5">
            {['7', '8', '9', '4', '5', '6', '1', '2', '3', '.', '0', 'backspace'].map(
              (key) => (
                <button
                  key={key}
                  type="button"
                  aria-label={key === 'backspace' ? 'Effacer le dernier chiffre' : key}
                  onClick={() =>
                    setRawPrice((current) => appendFreePriceKey(current, key))
                  }
                  className={`h-12 rounded-xl text-sm font-bold transition-all active:scale-95 ${
                    key === 'backspace'
                      ? 'bg-gray-700 text-red-400 hover:bg-gray-600'
                      : 'border border-gray-700 bg-gray-800 text-white hover:bg-gray-700'
                  }`}
                >
                  {key === 'backspace' ? <DeleteIcon size={16} className="mx-auto" /> : key}
                </button>
              ),
            )}
          </div>
        </div>

        <div className="px-5 pb-5 pt-2">
          <button
            type="button"
            disabled={!canAdd}
            onClick={() => {
              if (!canAdd) return;
              onAdd(designation.trim(), priceTtc);
              onClose();
            }}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#F4A261] py-3.5 font-black text-gray-900 transition-colors hover:bg-[#e8924f] disabled:cursor-not-allowed disabled:opacity-30"
          >
            <Plus size={15} />
            Ajouter au panier
          </button>
        </div>
      </div>
    </div>
  );
}
